import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { X, Images } from "lucide-react";
import { toast } from "sonner";

const MAX = 8;

export default function RecapPhotosEditor({ event, onChange }) {
  const [photos, setPhotos] = useState(event.recap_photos || []);
  const [busy, setBusy] = useState(false);

  // Re-sync when the selected event changes — otherwise switching between two past
  // events would briefly show the previous event's photos.
  useEffect(() => {
    setPhotos(event.recap_photos || []);
  }, [event.id, event.recap_photos]);

  const add = async (url) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${event.id}/recap-photos`, { image_url: url });
      setPhotos(data.recap_photos || []);
      onChange && onChange(data);
      toast.success("Recap photo added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add photo");
    } finally { setBusy(false); }
  };

  const remove = async (url) => {
    if (!window.confirm("Remove this recap photo?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/events/${event.id}/recap-photos`, { data: { image_url: url } });
      setPhotos(data.recap_photos || []);
      onChange && onChange(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`recap-editor-${event.id}`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>Event recap</div>
          <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
            {photos.length} / {MAX} photos · public on the event page
          </div>
        </div>
        {photos.length < MAX && (
          <ImageUploader
            label="Add recap photo"
            testId={`recap-upload-${event.id}`}
            onUploaded={add}
          />
        )}
      </div>
      {photos.length === 0 ? (
        <div
          data-testid={`recap-empty-${event.id}`}
          className="mt-4 rounded-sm border border-dashed p-8 text-center"
          style={{ borderColor: "rgba(31,93,52,0.35)", backgroundColor: "rgba(31,93,52,0.05)" }}
        >
          <Images className="h-6 w-6 mx-auto" style={{ color: "var(--terracotta)" }} />
          <div className="mt-3 font-serif text-lg">Share how the event went.</div>
          <p className="mt-1 text-xs max-w-md mx-auto" style={{ color: "var(--ink-soft)" }}>
            Upload 2–{MAX} photos of the crowd, booths and best moments. Recaps build trust for your future events
            and make vendors want to apply again.
          </p>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid={`recap-grid-${event.id}`}>
          {photos.map((p) => (
            <div key={p} className="relative group aspect-square rounded-sm overflow-hidden">
              <img src={p} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(p)}
                disabled={busy}
                data-testid={`recap-remove-${event.id}`}
                aria-label="Remove photo"
                className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export function RecapGallery({ photos }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mt-14" data-testid="event-recap-gallery">
      <div className="overline" style={{ color: "var(--moss)" }}>Event recap</div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {photos.map((p, i) => (
          <a
            key={p}
            href={p}
            target="_blank"
            rel="noreferrer"
            className="aspect-square rounded-sm overflow-hidden block hover:opacity-90 transition-opacity"
            data-testid={`recap-photo-${i}`}
          >
            <img src={p} alt="" className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}
